import React from 'react';
import { CheckCircle2, Download, KeyRound, AlertTriangle, XCircle } from 'lucide-react';

/**
 * Étape 4 : résultat de l'application. Deux variantes :
 *  - ÉCHEC (prop `echec`) : écran rouge dédié + raison + retour au rapport ;
 *  - SUCCÈS (prop `rapport`) : compteurs + comptes générés (non-GAR, §4c).
 * Les mots de passe ne vivent QUE dans ce rapport : affichés une fois,
 * téléchargeables en CSV, jamais re-consultables ensuite.
 */
function EtapeResultat({ rapport, echec, onNouvelImport, onRetourRapport }) {
    // ── Variante ÉCHEC ──
    if (echec) {
        return (
            <div className="space-y-5">
                <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-red-500 flex items-center justify-center">
                        <XCircle className="w-9 h-9 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-1">L'import n'a pas été appliqué</h3>
                    <p className="text-sm text-slate-500">Aucune modification n'a été enregistrée.</p>
                </div>
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-sm text-red-800">
                    <span className="font-semibold">Raison : </span>{echec}
                </div>
                <div className="flex justify-center gap-3">
                    <button
                        onClick={onRetourRapport}
                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/30 transition-all"
                    >
                        Revenir au rapport
                    </button>
                    <button
                        onClick={onNouvelImport}
                        className="text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-4 py-2 rounded-xl transition-colors"
                    >
                        Recommencer
                    </button>
                </div>
            </div>
        );
    }

    // ── Variante SUCCÈS ── (garde-fous sur les compteurs au cas où un panier manque)
    const identifiants = rapport.identifiants_crees || [];
    const n = (arr) => (Array.isArray(arr) ? arr.length : 0);

    const telechargerIdentifiants = () => {
        const lignes = [['Nom', 'Prénom', 'Classe', 'Identifiant', 'Mot de passe']]
            .concat(identifiants.map((c) => [c.nom, c.prenom, c.classe, c.username, c.mot_de_passe]));
        const csv = lignes.map((l) => l.map((v) => `"${v}"`).join(';')).join('\n');
        const blob = new Blob([String.fromCharCode(0xFEFF) + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'IdentifiantsElevesTanoEd.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-5">
            {/* Confirmation */}
            <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-emerald-500 flex items-center justify-center">
                    <CheckCircle2 className="w-9 h-9 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">Import appliqué !</h3>
                <p className="text-sm text-slate-500">L'établissement est à jour.</p>
            </div>

            {/* Compteurs */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-slate-700">{rapport.inchanges || 0}</p>
                    <p className="text-xs text-slate-500">déjà à jour</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-blue-700">{n(rapport.deplaces)}</p>
                    <p className="text-xs text-blue-600">changent de classe</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-emerald-700">{n(rapport.crees)}</p>
                    <p className="text-xs text-emerald-600">nouveaux</p>
                </div>
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-violet-700">{n(rapport.reactives)}</p>
                    <p className="text-xs text-violet-600">de retour</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-orange-700">{n(rapport.sortants)}</p>
                    <p className="text-xs text-orange-600">partis</p>
                </div>
            </div>

            {/* Comptes générés (non-GAR uniquement) */}
            {identifiants.length > 0 ? (
                <div className="border-2 border-blue-200 bg-blue-50 rounded-xl p-4">
                    <p className="text-sm font-bold text-blue-900 flex items-center gap-2 mb-1.5">
                        <KeyRound className="w-4 h-4" />
                        {identifiants.length} compte{identifiants.length > 1 ? 's' : ''} élève{identifiants.length > 1 ? 's' : ''} créé{identifiants.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-blue-800 flex items-start gap-1.5 mb-3">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        Téléchargez ce fichier maintenant et conservez-le : les mots de passe ne seront
                        plus jamais affichés.
                    </p>
                    <button
                        onClick={telechargerIdentifiants}
                        className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-lg shadow-blue-500/30 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Télécharger les identifiants (CSV)
                    </button>
                    <div className="border border-blue-200 rounded-xl overflow-hidden bg-white">
                        <div className="overflow-x-auto max-h-72 overflow-y-auto">
                            <table className="w-full text-xs sm:text-sm">
                                <thead className="bg-blue-600 text-white sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-semibold">Élève</th>
                                        <th className="px-3 py-2 text-left font-semibold">Classe</th>
                                        <th className="px-3 py-2 text-left font-semibold">Identifiant</th>
                                        <th className="px-3 py-2 text-left font-semibold">Mot de passe</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {identifiants.map((c, i) => (
                                        <tr key={i} className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-gray-800 font-medium">{c.nom} {c.prenom}</td>
                                            <td className="px-3 py-1.5 text-slate-600">{c.classe}</td>
                                            <td className="px-3 py-1.5 font-mono text-slate-700">{c.username}</td>
                                            <td className="px-3 py-1.5 font-mono text-slate-700">{c.mot_de_passe}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="text-center">
                <button
                    onClick={onNouvelImport}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-4 py-2 rounded-xl transition-colors"
                >
                    Faire un autre import
                </button>
            </div>
        </div>
    );
}

export default EtapeResultat;
